import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Button from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { AppView, HelpNavigationButton, IconArrow, Indicator } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import Amplitude from 'utils/amplitude';
import { capitalizeFirstCharacter, getFractionDigits } from 'utils';
import useHeaderHeight from 'hooks/useHeaderHeight';
import usePasscodeModal from 'hooks/usePasscodeModal';
import { selectFeatureFlag } from 'reducers/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useHelpActionSheet from 'hooks/useHelpActionSheet';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getPatternDayTradingInfo } from '../actions';
import { SHARE_QUANTITY_DECIMALS } from '../constants';
import AmountPreview from '../components/AmountPreview';
import QuantityInput from '../components/QuantityInput';
import { formatNumberOfShares, isValidQuantityStep, roundQuantity } from '../utils';
import { useOrderPreview, useOrderConfirm, usePosition, useStockMeta } from '../hooks';
import { useNavigateBackAfterSellFromPortfolio } from '../hooks/useNavigateBackAfterSellFromPortfolio';
import { amplitudeSource, useAmplitudeOnMountEvent } from '../utils/analytics';
import { SelectSellOrderTypeModal } from '../components/SelectSellOrderTypeModal';
import { SellOrderType } from '../types';
import { selectIsTradingAccountAlpaca, selectTradingAccount } from '../reducers/selectors';
const strings = {
    only: 'Only',
    available: 'available',
    button: 'Review order',
    invalidStep: (min) => `Shares must be sold in multiples of ${min}`,
    passcodeTitle: 'Enter Your Pin Code',
    sellAll: 'Sell all',
    sellingAll: 'You’re about to sell all your shares',
};
const DEFAULT_INITIAL_QUANTITY = 0;
const SellScreenComponent = ({ name, route, symbol, navigation }) => {
    const { assetId } = route.params;
    const colors = useColors();
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const accountType = tradingAccount?.account.accountType || 'GIA';
    const tradingAccountId = tradingAccount?.account?.id;
    const isAlpacaAccount = !!tradingAccount?.isAlpaca;
    const symbolOrName = symbol || name;
    const limitStopOrders = useAppSelector((store) => selectFeatureFlag(store, 'limit_stop_orders').value);
    const didViewSellStopOrderIntro = useAppSelector((store) => store.deviceSettings.didViewSellStopOrderIntro);
    const didViewSellLimitOrderIntro = useAppSelector((store) => store.deviceSettings.didViewSellLimitOrderIntro);
    const assetType = useAppSelector((store) => store.invest.caches[assetId]?.type);
    const minimumTransferUnit = useAppSelector((store) => store.invest.caches[assetId]?.minimumTransferUnit);
    useAmplitudeOnMountEvent(undefined, 'Order Screen', { type: 'sell', symbol: symbolOrName });
    const [position] = usePosition(assetId);
    const INITIAL_QUANTITY = DEFAULT_INITIAL_QUANTITY;
    const quantityRef = useRef(INITIAL_QUANTITY);
    const setQuantityRef = useRef(undefined);
    const [exceededMax, setExceededMax] = useState(false);
    const [isSellingAll, setIsSellingAll] = useState(false);
    const [isValidStep, setIsValidStep] = useState(true);
    const [fullOrderPreview, getOrderPreview, loading] = useOrderPreview(assetId, tradingAccountId, {
        initialAmount: INITIAL_QUANTITY,
        operation: 'sell',
        updateOnMount: true,
    });
    const orderPreview = fullOrderPreview?.order;
    const min = assetType === 'ETF' ? 0.0001 : minimumTransferUnit;
    const maximumFractionDigits = min !== undefined ? getFractionDigits(min) : SHARE_QUANTITY_DECIMALS;
    const minText = min !== undefined ? formatNumberOfShares(min, false, undefined, undefined, maximumFractionDigits) : '';
    const [confirm, execute] = useOrderConfirm(symbol, name, assetId, tradingAccountId, isAlpacaAccount, accountType, 'sell');
    const [getPin] = usePasscodeModal();
    const navigateBackAfterOrder = useNavigateBackAfterSellFromPortfolio();
    const onQuantityChange = useCallback((value) => {
        let newIsSellingAll = false;
        let quantity = value;
        if (position) {
            if (roundQuantity(value, maximumFractionDigits) === roundQuantity(position.availableToSell, maximumFractionDigits)) {
                newIsSellingAll = true;
                quantity = position.availableToSell;
            }
        }
        // position.availableToSell is trusted as already a valid multiple of `min`, since it's the user's actual holding
        const stepValid = newIsSellingAll || isValidQuantityStep(quantity, min, maximumFractionDigits);
        quantityRef.current = quantity;
        setIsValidStep(stepValid);
        if (stepValid) {
            getOrderPreview(quantity);
        }
        setIsSellingAll(newIsSellingAll);
        setExceededMax(false);
    }, [position, getOrderPreview, maximumFractionDigits, min]);
    const onSuccess = useCallback((order, marketOpeningInfo) => {
        navigateBackAfterOrder(isAlpacaAccount ? 1 : 2);
        navigation.navigate('InvestOrder', { order, marketOpeningInfo });
        if (isAlpacaAccount) {
            dispatch(getPatternDayTradingInfo());
        }
    }, [isAlpacaAccount, navigateBackAfterOrder]);
    const onConfirm = useCallback(async (orderPreview, pin) => {
        Modal.hide(); // hide order confirmation modal
        await execute(orderPreview, onSuccess, pin);
    }, [execute, onSuccess]);
    const onTapConfirm = useCallback(async (latestOrderPreview, forcePinAuthentication) => {
        // orderPreview will always be defined here, since the button that calls this callback is disabled when orderPreview is not yet defined
        if (latestOrderPreview) {
            const { operation, estimated: { quantity }, } = latestOrderPreview;
            const biometryDescription = `You are selling ${formatNumberOfShares(quantity)} of ${symbol ? `$${symbol}` : name}`;
            try {
                Amplitude.logEvent('Submitted Request', { source: amplitudeSource, type: operation, symbol });
                const pin = await getPin({
                    title: strings.passcodeTitle,
                    biometryDescription,
                    forcePinAuthentication: typeof forcePinAuthentication === 'boolean' ? forcePinAuthentication : false,
                });
                try {
                    await onConfirm(latestOrderPreview, pin);
                }
                catch (e) {
                    // Other errors are already handled in execute
                    if ('message' in e && e.message === 'Refresh token expired') {
                        onTapConfirm(latestOrderPreview, true);
                    }
                }
            }
            catch {
                // use cancelled
            }
        }
    }, [getPin, name, onConfirm, symbol]);
    const onSubmit = useCallback(() => {
        confirm({ operation: 'sell', quantity: quantityRef.current }, fullOrderPreview, onTapConfirm);
    }, [fullOrderPreview, confirm, onTapConfirm]);
    const onSellAll = useCallback(() => {
        if (setQuantityRef.current && position) {
            setIsSellingAll(true);
            setQuantityRef.current(position.availableToSell);
        }
    }, [position]);
    const numPadHeader = useMemo(() => {
        const availableText = position ? `${formatNumberOfShares(position.availableToSell)}` : '';
        let positionStatus = (<Text NumbersThin-14 Secondary centered>
        {availableText.split(' ')[0]}
        <Text TextThin-14 Secondary>{` ${availableText.split(' ')[1]} ${strings.available}`}</Text>
      </Text>);
        if (exceededMax) {
            positionStatus = (<Text TextThin-14 Red>
          {`${strings.only} `}
          <Text NumbersThin-14 Red centered>
            {availableText.split(' ')[0]}
          </Text>
          {` ${availableText.split(' ')[1]} ${strings.available}`}
        </Text>);
        }
        else if (!isValidStep) {
            positionStatus = (<Text TextThin-14 Red centered>
          {strings.invalidStep(minText)}
        </Text>);
        }
        return (<View style={styles.numPadHeader}>
        {isSellingAll ? (<Text TextThin-14 Secondary centered>
            {strings.sellingAll}
          </Text>) : null}
        {!!position && !isSellingAll && positionStatus}
        {!isSellingAll ? (<>
            <Spacer h={24}/>
            <Button square hug small brandReversed title={strings.sellAll} onPress={onSellAll}/>
            <Spacer h={12}/>
          </>) : (<Spacer h={70}/>)}
        <Spacer flex/>
        <AmountPreview operation="sell" assetId={assetId} loading={loading} latestPrice={orderPreview?.estimated.price} amount={orderPreview?.estimated.amount}/>
      </View>);
    }, [
        position,
        styles.numPadHeader,
        isSellingAll,
        exceededMax,
        isValidStep,
        minText,
        onSellAll,
        assetId,
        loading,
        orderPreview?.estimated.price,
        orderPreview?.estimated.amount,
    ]);
    const quantityInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
    }), [colors.text.primary]);
    const onExceedMax = useCallback(() => {
        setExceededMax(true);
    }, []);
    const headerHeight = useHeaderHeight();
    const paddingTop = useMemo(() => ({
        paddingTop: headerHeight * 0.6,
    }), [headerHeight]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container, paddingTop);
    const handleChooseOrderType = useCallback(() => {
        if (symbol) {
            SelectSellOrderTypeModal.show(symbol, assetId, (orderType) => {
                if (orderType === SellOrderType.MARKET) {
                    return;
                }
                if (position?.availableToSell) {
                    const hasWholePosition = !!Math.floor(position.availableToSell);
                    if (!hasWholePosition) {
                        Modal.showError(`${capitalizeFirstCharacter(orderType)} orders only work with whole shares. You currently hold a fraction of a share.`, undefined, 'Slow down 🤪');
                        return;
                    }
                    if (orderType === SellOrderType.LIMIT) {
                        if (!didViewSellLimitOrderIntro) {
                            navigation.navigateDeprecated('InvestLimitOrderIntro', { action: 'sell', assetId });
                            return;
                        }
                        navigation.navigateDeprecated('InvestSetLimitStopPrice', {
                            action: 'sell',
                            assetId,
                            orderType: SellOrderType.LIMIT,
                        });
                    }
                    if (orderType === SellOrderType.STOP) {
                        if (!didViewSellStopOrderIntro) {
                            navigation.navigateDeprecated('InvestStopOrderIntro', { action: 'sell', assetId });
                            return;
                        }
                        navigation.navigateDeprecated('InvestSetLimitStopPrice', {
                            action: 'sell',
                            assetId,
                            orderType: SellOrderType.STOP,
                        });
                    }
                }
            });
        }
    }, [assetId, didViewSellLimitOrderIntro, didViewSellStopOrderIntro, position?.availableToSell, symbol]);
    return (<AppView type="primary">
      <View style={paddingBottom}>
        <StatusBar barStyle="dark-content" animated/>
        <QuantityInput flex onExceedMax={onExceedMax} numPadHeader={numPadHeader} setValueRef={setQuantityRef} colors={quantityInputColors} max={position?.availableToSell} initialValue={INITIAL_QUANTITY} onValueChange={onQuantityChange} pinPadStyle={styles.pinPadStyle} inputFieldStyle={styles.inputFieldStyle} maximumFractionDigits={maximumFractionDigits}/>
        <Spacer h={20}/>
        <View style={styles.buttonContainer}>
          {limitStopOrders && isAlpacaAccount ? (<>
              <Button flex brandReversed title="Market order" onPress={handleChooseOrderType} rightIcon={<IconArrow direction="down" color={colors.text.textOnBrand}/>}/>
              <Spacer w={8}/>
            </>) : null}
          <Button flex brand title={strings.button} onPress={onSubmit} disabled={!fullOrderPreview || loading || !isValidStep}/>
        </View>
      </View>
    </AppView>);
};
const SellScreen = ({ route, navigation }) => {
    const { assetId } = route.params;
    const colors = useColors();
    const stockCache = useStockMeta(assetId);
    const showHelpActionSheet = useHelpActionSheet();
    const isTradingAccountAlpaca = useAppSelector(selectIsTradingAccountAlpaca);
    const symbol = stockCache?.symbol;
    const name = stockCache?.name;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: symbol && isTradingAccountAlpaca ? `Sell $${symbol}` : 'Sell',
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpNavigationButton black onPress={showHelpActionSheet}/>,
        });
    }, [colors.text.primary, isTradingAccountAlpaca, showHelpActionSheet, symbol]);
    if (name) {
        return <SellScreenComponent route={route} navigation={navigation} symbol={symbol} name={name}/>;
    }
    return (<AppView type="primary">
      <Indicator dark/>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    numPadHeader: {
        flex: 3,
        width: '100%',
        alignItems: 'center',
    },
    pinPadStyle: {
        marginTop: rem(10),
    },
    inputFieldStyle: {
        fontSize: rem(64),
        lineHeight: rem(87.3),
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingHorizontal: rem(16),
    },
}));
export default SellScreen;
